import { useTranslation } from 'react-i18next'
import { ConfigurationHeaderWrapper } from '../../../../../_components/configuration-header-wrapper.component'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { FollowupTimeline } from '../../../_components/followup-timeline.component'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { ChatbotGeneralForm } from '../../_components/chatbot-general-form/chatbot-general-form.component'
import { useSortedFollowupTimeline } from '../../../_components/use-sorted-followup-timeline.hook'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChatbotService } from 'src/services/chatbot.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useFollowupIdFromParams } from '../../../_components/use-followup-id-from-params.hook'
import { CurecallBadge, ECurecallBadgeColor } from '@honestica/curecall-ui'
import { ChatbotPublishButton } from '../_components/chatbot-publish-button.component'
import { useNotification } from 'src/providers/notification.provider'
import { useParams } from 'react-router-dom'
import { useLocale } from 'src/hooks/use-locale'

export default function ChatbotGeneralFormEditPage() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { followupId, chatbotId: chatbotIdParam } = useParams()
  const { locale } = useLocale()
  const { project } = useCurrentProject()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const projectId = project.id
  const followupId_ = useFollowupIdFromParams()
  const chatbotId = Number.parseInt(chatbotIdParam as string)

  const {
    isLoading: isLoadingTimeline,
    error: errorTimeline,
    followupTimeline,
    timelineMedEventsSorted,
  } = useSortedFollowupTimeline(followupId as string)

  const {
    data: chatbot,
    isLoading: isLoadingChatbot,
    error: errorChatbot,
  } = useQuery({
    queryKey: ['chatbot', chatbotId],
    queryFn: () => ChatbotService.get(projectId, followupId_!, chatbotId),
  })

  const publishMutation = useMutation({
    mutationFn: (isActive: boolean) => ChatbotService.publish(projectId, followupId_!, chatbotId, isActive),
    onSuccess: (_, isActive) => {
      queryClient.invalidateQueries({ queryKey: ['chatbot', chatbotId] })
      notify({
        title: t('common.actions.success'),
        message: isActive
          ? t('pages.configuration.followups.chatbot.publish-success')
          : t('pages.configuration.followups.chatbot.unpublish-success'),
        appearance: 'SUCCESS',
      })
    },
  })

  const isChatbotPublished = !!chatbot?.isActive

  const handlePublish = async () => {
    await publishMutation.mutateAsync(true)
  }

  const handleUnpublish = async () => {
    await publishMutation.mutateAsync(false)
  }

  if (errorTimeline || errorChatbot) return <ErrorQueryHandler error={errorTimeline || errorChatbot} />
  if (isLoadingTimeline || isLoadingChatbot) return <div className="loading" />

  return (
    <ConfigurationHeaderWrapper
      title={t('pages.configuration.followups.manage-followup', {
        followupName: getTranslatedLabel(locale, followupTimeline?.intlName),
      })}
      subTitle={t('pages.configuration.followups.description')}
      className="c-flex c-flex-row c-pb-10 c-relative"
    >
      <FollowupTimeline timelineMedEventsSorted={timelineMedEventsSorted} />
      <div className="c-bg-grey-200 c-w-px c-h-full" />
      <div className="c-w-full c-pl-4">
        <h2 className="c-text-xl c-font-bold c-mb-2 c-flex c-items-center c-gap-2">
          {t('pages.configuration.followups.chatbot.general-form.edit-title')}
          <CurecallBadge color={ECurecallBadgeColor.GREY} className="c-h-max">
            {isChatbotPublished
              ? t('pages.configuration.followups.chatbot.published')
              : t('pages.configuration.followups.chatbot.unpublished')}
          </CurecallBadge>
          <div className="c-text-sm">
            <ChatbotPublishButton
              isChatbotPublished={isChatbotPublished}
              onPublish={handlePublish}
              onUnpublish={handleUnpublish}
              followupId={followupId_!}
            />
          </div>
        </h2>
        <ChatbotGeneralForm chatbotToUpdate={chatbot} onCancel={() => navigate(-1)} />
      </div>
    </ConfigurationHeaderWrapper>
  )
}
